import { useRef, useState, useMemo, useEffect } from "react";
import Popup from "../components/Popup";
import UploadImage from "../components/UploadImage";
import ViewCarousel from "../components/ViewCarousel";
import { Toaster, toast } from "react-hot-toast";
import { getLevelFromUrl } from "../components/getLevelFromUrl";
import axios from "axios";
import { Upload, Eye, AlertTriangle } from "lucide-react";
import { isProdEnv } from "../../../../env-utils";

const ERLandingPage = ({ navigation }) => {
  const fileInputRef = useRef(null);
  const [diagrams, setDiagrams] = useState([]);
  const [showUpload, setShowUpload] = useState(false);
  const [viewDiagrams, setViewDiagrams] = useState([]);
  const [showView, setShowView] = useState(false);
  const [index, setIndex] = useState(0);
  const [zoom, setZoom] = useState(1);
  const [loadingView, setLoadingView] = useState(false);
  const [viewError, setViewError] = useState(false);
  const [isDirty, setIsDirty] = useState(false);              
  const [showConfirmClose, setShowConfirmClose] = useState(false); 
  const [loadingExisting, setLoadingExisting] = useState(false); 
  const [hasDiagrams, setHasDiagrams] = useState(false);
  const isProd = useMemo(() => isProdEnv(), []);  

  const defaultLevel = navigation?.level;
  const defaultValue = navigation?.selectedL1;

  const userEmail = localStorage.getItem("userId");
  const userRole = localStorage.getItem("role");

  useEffect(() => {
    const checkDiagrams = async () => {
      const { level, value } = getLevelFromUrl();
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/get-erDiagrams?level=${level ? level : defaultLevel?.toLowerCase()}&value=${value ? encodeURIComponent(value) : defaultValue}`,
        );
        setHasDiagrams(res.data.erDiagrams && res.data.erDiagrams.length > 0);
      } catch (err) {
        setHasDiagrams(false);
      }
    };
    checkDiagrams();
  }, [defaultLevel, defaultValue]);

  const activeDiagrams = diagrams.filter((d) => !d.isRemoved);
  const canSubmit =
    activeDiagrams.length > 0 &&
    activeDiagrams.every((d) => d.description.trim());

  const handleFileSelect = (files) => {
    setIsDirty(true);
    Array.from(files).forEach((file, idx) => {
      const reader = new FileReader();

      reader.onload = () => {
        setDiagrams((prev) => [
          ...prev,
          {
            id: Date.now() + idx,
            name: file.name,
            size: file.size,
            file,
            base64: reader.result,
            preview: URL.createObjectURL(file),
            description: "",
            isRemoved: false,
          },
        ]);
      };

      reader.readAsDataURL(file);
    });
  };

  const handleRemove = (id) => {
    setIsDirty(true);
    setDiagrams((prev) =>
      prev.map((d) => (d.id === id ? { ...d, isRemoved: true } : d)),
    );
  };

  const handleDescriptionChange = (id, value) => {
    setIsDirty(true);
    setDiagrams((prev) =>
      prev.map((d) => (d.id === id ? { ...d, description: value } : d)),
    );
  };

  const base64ToFile = async (base64, filename) => {
    const res = await fetch(base64);
    const blob = await res.blob();
    return new File([blob], filename, { type: blob.type });
  };

  const handleSubmit = async () => {
    const { level, value } = getLevelFromUrl();

    const formData = new FormData();

    formData.append("user_id", userEmail);
    formData.append("level", level ? level : defaultLevel?.toLowerCase());
    formData.append("value", value ? value : defaultValue);

    for (const diagram of diagrams) {
      if (diagram.isRemoved) continue;

      let fileToSend = diagram.file;

      if (!fileToSend && diagram.base64) {
        fileToSend = await base64ToFile(diagram.base64, diagram.name);
      }

      if (fileToSend) {
        formData.append("files", fileToSend);
        formData.append("descriptions", diagram.description || "");
      }
    }

    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL}/upload-erDiagrams`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } },
      );

      toast.success("ER Diagrams uploaded successfully");
      setIsDirty(false);
      setShowUpload(false);
      setHasDiagrams(true);
    } catch (error) {
      console.error(error);
      toast.error("Upload failed");
      setShowUpload(false);
    }
  };

  const handleCloseUpload = () => {
    if (isDirty) {
      setShowConfirmClose(true);
    } else {
      setShowUpload(false);
    }
  };

  const handleConfirmDiscard = () => {
    setShowConfirmClose(false);
    setShowUpload(false);
    setIsDirty(false);
    setDiagrams([]);
  };

  const openViewModal = async () => {
    const { level, value } = getLevelFromUrl();

    setLoadingView(true);
    setViewError(false);
    setShowView(true);
    setIndex(0);
    setZoom(1);

    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/get-erDiagrams?level=${level ? level : defaultLevel?.toLowerCase()}&value=${value ? encodeURIComponent(value) : defaultValue}`,
      );

      const formatted = res.data.erDiagrams.map((d) => ({
        name: d.fileName,
        description: d.description,
        preview: d.imageData,
      }));

      setViewDiagrams(formatted);
    } catch (err) {
      console.error(err);
      setViewError(true);
      toast.error("Unable to load ER diagrams. Showing sample data.");
    } finally {
      setLoadingView(false);
    }
  };

  const openUploadModal = async () => {
    const { level, value } = getLevelFromUrl();

    setIsDirty(false);
    setShowConfirmClose(false);
    setShowUpload(true);
    setLoadingExisting(true);

    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/get-erDiagrams?level=${level ? level : defaultLevel?.toLowerCase()}&value=${value ? encodeURIComponent(value) : defaultValue}`,
      );

      const existing = res.data.erDiagrams.map((d, idx) => ({
        id: `existing-${idx}`,
        name: d.fileName,
        size: d.size || 0,
        base64: d.imageData,
        preview: d.imageData,
        description: d.description || "",
        isExisting: true,
        isRemoved: false,
      }));

      setDiagrams(existing);
    } catch (err) {
      console.warn("No existing diagrams found");
      setDiagrams([]);
    } finally {
      setLoadingExisting(false);
    }
  };

  return (
    <>
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: { fontWeight: 500 },
        }}
      />

    <div className="flex gap-2">
    {userRole === "Admin" && !isProd && (
      <button
        onClick={openUploadModal}
        className="h-[40px] text-[#fff] hover:text-white transition-colors duration-200 p-2 rounded-lg flex items-center gap-2 bg-[#357EDB] px-4 disabled:bg-gray-400 disabled:cursor-not-allowed disabled:text-white"
        disabled={isProd}
      >
        <Upload className="h-5" />
        Upload Design Diagrams
      </button>
    )}

    {hasDiagrams && (
      <button
        onClick={openViewModal}
        className="h-[40px] text-[#000] border border-[#D8C7E6] transition-colors duration-200 p-2 rounded-lg flex items-center gap-2 px-4 disabled:bg-gray-400 disabled:cursor-not-allowed disabled:text-white"
      >
        <Eye className="h-5" />
        View Design Diagrams
      </button>
    )}
    </div>

     

      {showUpload && (
        <Popup
          title="Upload Design Diagrams"
          onClose={handleCloseUpload}
          maxWidth="1100px"
          height="85vh"
        >
          <UploadImage
            diagrams={diagrams}
            fileInputRef={fileInputRef}
            onFileSelect={handleFileSelect}
            onRemove={handleRemove}
            onDescriptionChange={handleDescriptionChange}
            onSubmit={handleSubmit}
            canSubmit={canSubmit}
            loadingExisting={loadingExisting}
          />
        </Popup>
      )}

      {showConfirmClose && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-[420px] p-6 flex flex-col gap-4">
            <div className="flex items-start gap-3">
              <div className="mt-0.5 bg-amber-100 rounded-full p-2 shrink-0">
                <AlertTriangle className="text-amber-500 h-5 " />
              </div>
              <div>
                <h2 className="text-base font-semibold text-gray-900">
                  Unsaved Changes
                </h2>
                <p className="text-sm text-gray-500 mt-1 leading-relaxed">
                  You have unsaved changes that haven't been submitted yet.
                  Closing now will discard all your modifications — added or
                  removed diagrams and descriptions will be lost.
                </p>
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-1">
              <button
                onClick={() => setShowConfirmClose(false)}
                className="px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Keep Editing
              </button>
              <button
                onClick={handleConfirmDiscard}
                className=" text-sm bg-red-100 text-red-600 px-4 py-2 rounded-md transition-colors"
              >
                Discard & Close
              </button>
            </div>
          </div>
        </div>
      )}

      {showView && (
        <Popup
          title="View Design Diagrams"
          onClose={() => setShowView(false)}
          maxWidth="1600px"
          height="92vh"
          minHeight="90vh"
        >
          {loadingView ? (
            <div className="h-[420px] flex items-center justify-center text-gray-500">
              Loading Design Diagrams...
            </div>
          ) : (
            <ViewCarousel
              diagrams={viewDiagrams}
              index={index}
              setIndex={setIndex}
              zoom={zoom}
              setZoom={setZoom}
              resetZoom={() => setZoom(1)}
            />
          )}
        </Popup>
      )}
    </>
  );
};

export default ERLandingPage;